import { DefaultChatTransport, type ToolUIPart, type UIMessage } from "ai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getAuthToken } from "@/data-access/core/tokenManagement";
import {
  chatConversationStreamUrl,
  chatGenerateUrl,
} from "@/data-access/herald-chat/chat-http";
import type { FeedArticleProjectionFilterInput } from "@/data-access/shared/types/feed-article-projection.types";
import useAuth from "@/store/auth.slice";
import useHeraldChatSettingsStore from "@/store/herald-chat/herald-chat-settings.slice";

/** Id/title in refs keep DefaultChatTransport memoized; setRenderTick re-renders when response headers update refs. */
const CHAT_ID = "herald";

const isAssistantToolPart = (part: unknown): boolean => {
  if (part == null || typeof part !== "object") return false;
  const type = (part as { type?: unknown }).type;
  return (
    typeof type === "string" &&
    (type === "dynamic-tool" || type.startsWith("tool-"))
  );
};

// Typed against `ToolUIPart["state"]` so an SDK state rename fails the build.
const REPLAYABLE_TOOL_STATES = new Set<ToolUIPart["state"]>([
  "output-available",
  "output-error",
  "output-denied",
]);

// Replayable = terminal pair the adapter can reconstruct, minus provider-executed
// builtins (`srvtoolu_*`) whose tool_result is opaque server state. Caller must
// have confirmed `isAssistantToolPart(part)`.
const isPairedTerminalToolPart = (part: Record<string, unknown>): boolean => {
  if (part.providerExecuted === true) return false;
  return REPLAYABLE_TOOL_STATES.has(part.state as ToolUIPart["state"]);
};

const DROP_PART = Symbol("drop-outbound-part");

// Keep non-tool parts, drop orphan-prone tool parts, default nullish `input` to
// `{}` on kept ones. See anthropics/claude-code#6836, vercel/ai#14259, #14379.
const classifyOutboundPart = (part: unknown): unknown | typeof DROP_PART => {
  if (!isAssistantToolPart(part)) return part;
  const p = part as Record<string, unknown>;
  if (!isPairedTerminalToolPart(p)) return DROP_PART;
  return p.input == null ? { ...p, input: {} } : part;
};

const sanitizeOutboundAssistantMessage = (
  message: Record<string, unknown>,
): Record<string, unknown> | null => {
  if (message.role !== "assistant" || !Array.isArray(message.parts)) {
    return message;
  }

  let mutated = false;
  const parts: unknown[] = [];
  for (const part of message.parts) {
    const next = classifyOutboundPart(part);
    if (next === DROP_PART) {
      mutated = true;
      continue;
    }
    if (next !== part) mutated = true;
    parts.push(next);
  }

  if (!mutated) {
    return message;
  }

  // Dropping a now-empty assistant turn can leave adjacent user turns; that's
  // safe because naapi's pydantic-ai MessagesBuilder coalesces same-role
  // messages downstream (a placeholder would trip Anthropic's empty-text 400).
  return parts.length > 0 ? { ...message, parts } : null;
};

// Keeps terminal tool-call pairs on outbound history so the model retains
// tool-call continuity across turns and doesn't gaslight itself on follow-ups.
export const sanitizeOutboundChatRequestBody = (body: string): string => {
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(body) as Record<string, unknown>;
  } catch {
    return body;
  }

  if (!Array.isArray(parsed.messages)) {
    return body;
  }

  let changed = false;
  const messages = parsed.messages.flatMap((message) => {
    if (message == null || typeof message !== "object") {
      return [message];
    }

    // `metadata` is UI-only (e.g. restored `createdAt`); naapi does not need it.
    const { metadata: _metadata, ...withoutMetadata } = message as Record<
      string,
      unknown
    >;
    const sanitized = sanitizeOutboundAssistantMessage(
      "metadata" in message
        ? withoutMetadata
        : (message as Record<string, unknown>),
    );
    if (sanitized !== message) {
      changed = true;
    }
    return sanitized == null ? [] : [sanitized];
  });

  return changed ? JSON.stringify({ ...parsed, messages }) : body;
};

const sanitizeChatRequestInit = (
  init: RequestInit | undefined,
): RequestInit | undefined => {
  if (typeof init?.body !== "string") {
    return init;
  }

  const body = sanitizeOutboundChatRequestBody(init.body);
  return body === init.body ? init : { ...init, body };
};

/**
 * Server-Sent Events (SSE) for one assistant turn, as received. naapi stamps each buffered event with
 * `id: <redis stream entry id>`. `committedEventText` holds only complete events that carry an id, so it
 * always equals the server replay buffer up to `lastEventId`.
 */
export interface ConversationStreamEventLog {
  conversationId: string;
  assistantMessageId: string | null;
  lastEventId: string | null;
  committedEventText: string;
  /** True after the response body ended normally (not on a drop, abort, or page unload). */
  isStreamComplete: boolean;
}

/** Why the last chat request failed in a way HeraldChat recovers from without an error banner. */
export type ConversationStreamFailure =
  | "stream_interrupted"
  | "conversation_run_active";

/** sessionStorage key that carries an unfinished stream event log across a page reload. */
export const CONVERSATION_STREAM_EVENT_LOG_STORAGE_KEY =
  "herald-chat-stream-event-log";

const LAST_EVENT_ID_HEADER = "Last-Event-ID";

/** Returns the `id:` field of one SSE event block (lines without the blank-line separator), or null. */
const readServerSentEventId = (eventBlock: string): string | null => {
  for (const line of eventBlock.split("\n")) {
    if (line.startsWith("id:")) {
      // SSE spec: drop one optional space after the colon.
      return line.slice(3).replace(/^ /, "");
    }
  }
  return null;
};

const isAbortError = (error: unknown): boolean =>
  (error as { name?: unknown } | null)?.name === "AbortError";

/**
 * Passes SSE bytes to the AI SDK unchanged while it appends each complete event with an `id:` to
 * `streamEventLog`. Events without an id (error notices that naapi does not buffer) are not logged.
 *
 * `replayPrefixText` is enqueued first. The AI SDK builds a fresh assistant message on every resume
 * and needs the `start` and `text-start` chunks, so a `Last-Event-ID` gap replay must be preceded by
 * the events this client already received.
 */
export const recordConversationStreamEvents = (
  body: ReadableStream<Uint8Array>,
  streamEventLog: ConversationStreamEventLog,
  replayPrefixText: string,
  onStreamInterrupted: () => void,
): ReadableStream<Uint8Array> => {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let pendingEventText = "";

  return new ReadableStream<Uint8Array>({
    start(controller) {
      if (replayPrefixText !== "") {
        controller.enqueue(new TextEncoder().encode(replayPrefixText));
      }
    },
    async pull(controller) {
      let readResult: ReadableStreamReadResult<Uint8Array>;
      try {
        readResult = await reader.read();
      } catch (error) {
        if (!isAbortError(error)) onStreamInterrupted();
        controller.error(error);
        return;
      }
      if (readResult.done) {
        streamEventLog.isStreamComplete = true;
        controller.close();
        return;
      }
      controller.enqueue(readResult.value);

      pendingEventText += decoder.decode(readResult.value, { stream: true });
      let separatorIndex = pendingEventText.indexOf("\n\n");
      while (separatorIndex !== -1) {
        const eventBlock = pendingEventText.slice(0, separatorIndex);
        pendingEventText = pendingEventText.slice(separatorIndex + 2);
        const eventId = readServerSentEventId(eventBlock);
        if (eventId != null) {
          streamEventLog.committedEventText += `${eventBlock}\n\n`;
          streamEventLog.lastEventId = eventId;
        }
        separatorIndex = pendingEventText.indexOf("\n\n");
      }
    },
    cancel(reason) {
      return reader.cancel(reason);
    },
  });
};

/** Reads the stream event log saved by the previous page before a reload. Pure, so StrictMode can call it twice. */
const readSavedConversationStreamEventLog = (
  conversationId: string | undefined,
): ConversationStreamEventLog | null => {
  try {
    const savedText = sessionStorage.getItem(
      CONVERSATION_STREAM_EVENT_LOG_STORAGE_KEY,
    );
    if (savedText == null || conversationId == null) return null;
    const savedLog = JSON.parse(savedText) as ConversationStreamEventLog;
    return savedLog.conversationId === conversationId ? savedLog : null;
  } catch {
    return null;
  }
};

export interface UseChatTransportOptions {
  conversationId?: string;
  /** Adds `mode: analysis` to the generate request body. */
  mode?: "analysis";
  filterContext?: FeedArticleProjectionFilterInput | null;
}

const buildChatHeaders = async (): Promise<Record<string, string>> => {
  const { accessToken } = useAuth.getState();
  const token = await getAuthToken(accessToken ?? "");
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

export interface UseChatTransportReturn {
  transport: DefaultChatTransport<UIMessage>;
  chatId: string;
  /** Snapshot for this render; use getTransportConversationId in effects after header-driven updates. */
  conversationId: string | null;
  /** Ref-backed id (avoids stale closures vs. the snapshot above). */
  getTransportConversationId: () => string | null;
  /** Ref-backed title from X-Conversation-Title when the API exposes it (CORS may hide headers). */
  getTransportConversationTitle: () => string | null;
  /** Ref-backed turn id from X-Assistant-Message-Id for explicit server cancellation. */
  getTransportAssistantMessageId: () => string | null;
  /** Runs immediately after a successful resume GET and before replay processing starts. */
  setConversationResumeAcceptedHandler: (
    handler: ((assistantMessageId: string | null) => void) | null,
  ) => void;
  getConversationResumeResult: () => "active" | "inactive" | null;
  /** Recoverable failure of the latest generate or resume request; null after success or a non-recoverable error. */
  getConversationStreamFailure: () => ConversationStreamFailure | null;
  /** Last SSE `id:` received for the active turn; a change means new events arrived. */
  getConversationStreamLastEventId: () => string | null;
  disconnectConversationStream: () => void;
  resetConversation: () => void;
  /** Clears cached title only when switching persisted thread id, not on first bind after a new chat. */
  setConversationId: (id: string) => void;
}

const useChatTransport = (
  options?: UseChatTransportOptions,
): UseChatTransportReturn => {
  const routedConversationIdRef = useRef(options?.conversationId);
  const conversationIdRef = useRef<string | null>(
    options?.conversationId ?? null,
  );
  const conversationTitleRef = useRef<string | null>(null);
  const assistantMessageIdRef = useRef<string | null>(null);
  const conversationResumeAcceptedHandlerRef = useRef<
    ((assistantMessageId: string | null) => void) | null
  >(null);
  const conversationResumeResultRef = useRef<"active" | "inactive" | null>(
    null,
  );
  const conversationStreamAbortControllerRef = useRef<AbortController | null>(
    null,
  );
  const [savedConversationStreamEventLog] = useState(() =>
    readSavedConversationStreamEventLog(options?.conversationId),
  );
  const conversationStreamEventLogRef =
    useRef<ConversationStreamEventLog | null>(savedConversationStreamEventLog);
  const conversationStreamFailureRef = useRef<ConversationStreamFailure | null>(
    null,
  );

  if (routedConversationIdRef.current !== options?.conversationId) {
    routedConversationIdRef.current = options?.conversationId;
    const nextConversationId = options?.conversationId ?? null;
    if (nextConversationId !== conversationIdRef.current) {
      conversationIdRef.current = nextConversationId;
      conversationTitleRef.current = null;
      assistantMessageIdRef.current = null;
      conversationResumeResultRef.current = null;
      conversationResumeAcceptedHandlerRef.current = null;
      conversationStreamEventLogRef.current = null;
    }
  }

  // A reload aborts the stream; keep the unfinished log so the next page resumes from its cursor.
  useEffect(() => {
    sessionStorage.removeItem(CONVERSATION_STREAM_EVENT_LOG_STORAGE_KEY);
    const saveUnfinishedStreamEventLog = () => {
      const streamEventLog = conversationStreamEventLogRef.current;
      if (
        streamEventLog == null ||
        streamEventLog.isStreamComplete ||
        streamEventLog.lastEventId == null
      ) {
        return;
      }
      try {
        sessionStorage.setItem(
          CONVERSATION_STREAM_EVENT_LOG_STORAGE_KEY,
          JSON.stringify(streamEventLog),
        );
      } catch {
        // Quota exceeded: the next page replays the turn from the start instead.
      }
    };
    window.addEventListener("pagehide", saveUnfinishedStreamEventLog);
    return () =>
      window.removeEventListener("pagehide", saveUnfinishedStreamEventLog);
  }, []);
  const modeRef = useRef<"analysis" | undefined>(options?.mode);
  modeRef.current = options?.mode;
  const filterContextRef = useRef<FeedArticleProjectionFilterInput | null>(
    options?.filterContext ?? null,
  );
  filterContextRef.current = options?.filterContext ?? null;
  /** Forces a render when headers bump id/title refs so returned conversationId stays in sync. */
  const [, setRenderTick] = useState(0);

  const transport = useMemo(() => {
    const fetchWithHeaders: typeof globalThis.fetch = async (input, init) => {
      const method = init?.method ?? "GET";
      const resolvedUrl =
        typeof input === "string"
          ? input
          : input instanceof URL
            ? input.href
            : input.url;
      const isConversationStreamRequest =
        method === "GET" && resolvedUrl.endsWith("/stream");
      if (
        isConversationStreamRequest &&
        conversationIdRef.current == null &&
        resolvedUrl.startsWith(`${chatGenerateUrl}/`)
      ) {
        conversationResumeResultRef.current = "inactive";
        return new Response(null, { status: 204 });
      }

      let requestInit = sanitizeChatRequestInit(init);
      if (isConversationStreamRequest) {
        conversationStreamAbortControllerRef.current?.abort();
        const abortController = new AbortController();
        conversationStreamAbortControllerRef.current = abortController;
        requestInit = { ...requestInit, signal: abortController.signal };
      }

      conversationStreamFailureRef.current = null;
      const sentLastEventId = isConversationStreamRequest
        ? new Headers(requestInit?.headers).get(LAST_EVENT_ID_HEADER)
        : null;
      let response: Response;
      try {
        response = await globalThis.fetch(input, requestInit);
      } catch (error) {
        // A failed generate POST may not have reached naapi, so only resume GETs retry.
        if (isConversationStreamRequest && !isAbortError(error)) {
          conversationStreamFailureRef.current = "stream_interrupted";
        }
        throw error;
      }

      const newConversationId = response.headers.get("X-Conversation-Id");
      if (
        newConversationId &&
        newConversationId !== conversationIdRef.current
      ) {
        conversationIdRef.current = newConversationId;
        setRenderTick((t) => t + 1);
      }

      const assistantMessageId = response.headers.get("X-Assistant-Message-Id");
      if (
        assistantMessageId &&
        assistantMessageId !== assistantMessageIdRef.current
      ) {
        assistantMessageIdRef.current = assistantMessageId;
        setRenderTick((t) => t + 1);
      }

      const titleHeader = response.headers.get("X-Conversation-Title");
      if (titleHeader != null) {
        const trimmed = titleHeader.trim();
        if (trimmed !== "" && trimmed !== conversationTitleRef.current) {
          conversationTitleRef.current = trimmed;
          setRenderTick((t) => t + 1);
        }
      }

      if (method === "POST" && response.status === 409) {
        const conflictBody = (await response
          .clone()
          .json()
          .catch(() => null)) as { code?: unknown } | null;
        if (conflictBody?.code === "conversation_run_active") {
          conversationStreamFailureRef.current = "conversation_run_active";
        }
        return response;
      }
      if (isConversationStreamRequest && response.status >= 500) {
        conversationStreamFailureRef.current = "stream_interrupted";
      }

      const isEventStreamResponse =
        response.ok && response.status !== 204 && response.body != null;
      const responseAssistantMessageId = response.headers.get(
        "X-Assistant-Message-Id",
      );
      // Gap replay: continue the same log and re-feed its events before the server's new ones.
      let gapReplayEventLog: ConversationStreamEventLog | null = null;
      if (isEventStreamResponse && sentLastEventId != null) {
        const previousEventLog = conversationStreamEventLogRef.current;
        if (
          previousEventLog == null ||
          previousEventLog.assistantMessageId !== responseAssistantMessageId
        ) {
          // The cursor belongs to an earlier turn; replay the running turn from its first event.
          await response.body?.cancel();
          conversationStreamEventLogRef.current = null;
          const headersWithoutCursor = new Headers(init?.headers);
          headersWithoutCursor.delete(LAST_EVENT_ID_HEADER);
          return fetchWithHeaders(input, {
            ...init,
            headers: headersWithoutCursor,
          });
        }
        gapReplayEventLog = previousEventLog;
      }

      if (isConversationStreamRequest) {
        if (response.status === 204) {
          conversationResumeResultRef.current = "inactive";
          conversationResumeAcceptedHandlerRef.current = null;
        } else if (response.ok) {
          conversationResumeResultRef.current = "active";
          const handleResumeAccepted =
            conversationResumeAcceptedHandlerRef.current;
          conversationResumeAcceptedHandlerRef.current = null;
          handleResumeAccepted?.(assistantMessageIdRef.current);
        }
      }

      if (!isEventStreamResponse || response.body == null) {
        return response;
      }
      const replayPrefixText = gapReplayEventLog?.committedEventText ?? "";
      const streamEventLog: ConversationStreamEventLog = gapReplayEventLog ?? {
        conversationId: conversationIdRef.current ?? "",
        assistantMessageId: responseAssistantMessageId,
        lastEventId: null,
        committedEventText: "",
        isStreamComplete: false,
      };
      streamEventLog.isStreamComplete = false;
      conversationStreamEventLogRef.current = streamEventLog;
      const recordedBody = recordConversationStreamEvents(
        response.body,
        streamEventLog,
        replayPrefixText,
        () => {
          conversationStreamFailureRef.current = "stream_interrupted";
        },
      );
      return new Response(recordedBody, {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      });
    };

    return new DefaultChatTransport<UIMessage>({
      api: chatGenerateUrl,
      headers: buildChatHeaders,
      prepareReconnectToStreamRequest: async ({
        headers: reconnectHeaders,
      }) => {
        const conversationId = conversationIdRef.current;
        if (!conversationId) {
          return {};
        }
        const auth = await buildChatHeaders();
        const merged = new Headers(reconnectHeaders ?? undefined);
        for (const [key, value] of Object.entries(auth)) {
          merged.set(key, value);
        }
        // naapi resume_view replays only events after this cursor; without it, from `0-0`.
        const streamEventLog = conversationStreamEventLogRef.current;
        if (
          streamEventLog?.conversationId === conversationId &&
          streamEventLog.lastEventId != null
        ) {
          merged.set(LAST_EVENT_ID_HEADER, streamEventLog.lastEventId);
        }
        return {
          api: chatConversationStreamUrl(conversationId),
          headers: merged,
        };
      },
      body: () => {
        const extra: Record<string, unknown> = {};
        if (conversationIdRef.current) {
          extra.conversation_id = conversationIdRef.current;
        }
        extra.deep_analysis =
          useHeraldChatSettingsStore.getState().isDeepAnalysisEnabled;
        const mode = modeRef.current;
        if (mode === "analysis") {
          extra.mode = "analysis";
        }
        if (filterContextRef.current != null) {
          extra.filter_context = filterContextRef.current;
        }
        return extra;
      },
      fetch: fetchWithHeaders,
    });
  }, []);

  const resetConversation = useCallback(() => {
    conversationIdRef.current = null;
    conversationTitleRef.current = null;
    assistantMessageIdRef.current = null;
    conversationResumeResultRef.current = null;
    conversationResumeAcceptedHandlerRef.current = null;
    conversationStreamEventLogRef.current = null;
    setRenderTick((t) => t + 1);
  }, []);

  const setConversationId = useCallback((id: string) => {
    const previousId = conversationIdRef.current;
    conversationIdRef.current = id;
    if (previousId != null && previousId !== id) {
      conversationTitleRef.current = null;
      assistantMessageIdRef.current = null;
      conversationResumeResultRef.current = null;
      conversationResumeAcceptedHandlerRef.current = null;
      conversationStreamEventLogRef.current = null;
    }
    setRenderTick((t) => t + 1);
  }, []);

  const getTransportConversationId = useCallback(
    (): string | null => conversationIdRef.current,
    [],
  );

  const getTransportConversationTitle = useCallback(
    (): string | null => conversationTitleRef.current,
    [],
  );

  const getTransportAssistantMessageId = useCallback(
    (): string | null => assistantMessageIdRef.current,
    [],
  );

  const setConversationResumeAcceptedHandler = useCallback(
    (handler: ((assistantMessageId: string | null) => void) | null): void => {
      conversationResumeAcceptedHandlerRef.current = handler;
      conversationResumeResultRef.current = null;
    },
    [],
  );

  const getConversationResumeResult = useCallback(
    (): "active" | "inactive" | null => conversationResumeResultRef.current,
    [],
  );

  const getConversationStreamFailure = useCallback(
    (): ConversationStreamFailure | null =>
      conversationStreamFailureRef.current,
    [],
  );

  const getConversationStreamLastEventId = useCallback(
    (): string | null =>
      conversationStreamEventLogRef.current?.lastEventId ?? null,
    [],
  );

  const disconnectConversationStream = useCallback((): void => {
    conversationStreamAbortControllerRef.current?.abort();
    conversationStreamAbortControllerRef.current = null;
  }, []);

  return {
    transport,
    chatId: CHAT_ID,
    conversationId: conversationIdRef.current,
    getTransportConversationId,
    getTransportConversationTitle,
    getTransportAssistantMessageId,
    setConversationResumeAcceptedHandler,
    getConversationResumeResult,
    getConversationStreamFailure,
    getConversationStreamLastEventId,
    disconnectConversationStream,
    resetConversation,
    setConversationId,
  };
};

export default useChatTransport;
